<script setup lang="ts">
import type { CarouselItemSelectorProps } from '.';
import type { HTMLAttributes } from 'vue';

import { cn } from '@/utils';
import { useMotion } from '@vueuse/motion';

import {
	carouselItemSelectorBarStyles,
	carouselItemSelectorContainerStyles
} from '.';

const props = withDefaults(defineProps<CarouselItemSelectorProps>(), {
	variant: 'white',
	active: false,
	delay: 1000
});

enum SelectorOperations {
	FILLING = 'filling',
	DRYING = 'drying'
}

const operationName = ref<SelectorOperations>(SelectorOperations.FILLING);
const whiteBarRef = ref<HTMLElement | undefined>(undefined);

const motionConfig = {
	initial: { scaleX: 0, transformOrigin: 'left' },
	enter: {
		scaleX: 1,
		transformOrigin: 'left',
		transition: { duration: props.delay }
	},
	leave: {
		scaleX: 0,
		transformOrigin: 'right',
		transition: { duration: props.delay / 3 }
	}
};

const { apply: applyAnimation, stop: stopAnimation } = useMotion(
	whiteBarRef,
	motionConfig
);

async function startAnimation() {
	operationName.value = SelectorOperations.FILLING;
	applyAnimation('enter');
}

function endAnimation() {
	operationName.value = SelectorOperations.DRYING;
	applyAnimation('leave');
}

const isFilling = computed(
	() => operationName.value === SelectorOperations.FILLING
);

watch(
	() => props.active,
	isActive => {
		if (isActive) {
			startAnimation();
		} else {
			endAnimation();
		}
	},
	{ immediate: true }
);

const isDynamicWidth = computed(
	() => props.dynamicWidth && !isNaN(props.dynamicWidth)
);

const style = computed(() => ({
	...(isDynamicWidth.value
		? {
				width: `${props.dynamicWidth}px`,
				maxWidth: `60px`,
				minWidth: `5px`,
				flexGrow: 1,
				flexShrink: 0
			}
		: {})
}));

defineExpose({ stopAnimation });
</script>

<template>
	<button
		:class="[
			cn(
				carouselItemSelectorContainerStyles({
					variant,
					dynamicWidth: Boolean(isDynamicWidth)
				}),
				props.class
			),
			isFilling ? 'justify-start' : 'justify-end'
		]"
		:style="style"
	>
		<div
			ref="whiteBarRef"
			:class="carouselItemSelectorBarStyles({ variant })"
		></div>
	</button>
</template>
